import { useEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'

import { MediaStatus } from 'types/media'
import { projectDataSelectors } from 'store/features/projectData/selectors'
import { useWebWoodContext } from 'context/WebWoodContext'
import { EVENT_TYPES, useSubscribeToEvent } from 'services/event-bus'
import { generationProgressService } from 'services/generationProgress'

export const useLoadingMediasState = () => {
  const [loadingStates, setLoadingStates] = useState<Record<string, number>>({})
  const loadingMediasRef = useRef<Record<string, string>>({})

  const { updateObjectById } = useWebWoodContext()
  const mediaItems = useSelector(projectDataSelectors.mediaItems)

  const loadingMedias = useMemo(
    () => mediaItems.filter(media => media.meta.status === MediaStatus.PENDING),
    [mediaItems]
  )

  useEffect(() => {
    const nextMap: Record<string, string> = {}
    loadingMedias.forEach(media => {
      nextMap[media.id] = String(media.assetId)
    })

    const previousIds = Object.keys(loadingMediasRef.current)
    const nextIds = Object.keys(nextMap)

    const addedIds = nextIds.filter(id => !(id in loadingMediasRef.current))
    const removedIds = previousIds.filter(id => !(id in nextMap))

    addedIds.forEach(id => {
      if (!generationProgressService.isActive(id)) {
        generationProgressService.resume(id, nextMap[id])
      }
    })

    loadingMediasRef.current = nextMap

    if (addedIds.length === 0 && removedIds.length === 0) return

    setLoadingStates(prev => {
      const next = { ...prev }
      addedIds.forEach(id => {
        next[id] = generationProgressService.getProgress(id) ?? 0
      })
      removedIds.forEach(id => {
        delete next[id]
      })
      return next
    })
  }, [loadingMedias])

  useSubscribeToEvent(
    EVENT_TYPES.GENERATION_PROGRESS_UPDATE,
    (
      mediaItemId: string,
      assetId: string,
      progress: number,
      _message: string,
      _isActive: boolean
    ) => {
      if (!(mediaItemId in loadingMediasRef.current)) return

      setLoadingStates(prev =>
        prev[mediaItemId] === progress
          ? prev
          : { ...prev, [mediaItemId]: progress }
      )

      const engineId = `${mediaItemId}-${assetId}`
      updateObjectById(engineId, {
        metadata: { progress }
      } as Record<string, unknown>)
    }
  )

  return loadingStates
}
